import { useEffect, useMemo, useState } from 'react';

import { toCandidateViewModel } from '@/adapters/candidate-adapter';
import type { Candidate } from '@/models/candidate';
import { getCandidateBySlug } from '@/services/candidate-service';
import { getCandidateProposalDocument, type CandidateProposalDocumentResponse } from '@/services/candidate-proposal-service';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';

type CandidateProposalState =
  | { status: 'loading' }
  | { status: 'success'; candidate: Candidate; document: CandidateProposalDocumentResponse }
  | { status: 'error'; error: string };

export function useCandidateProposal(slug: string | null): CandidateProposalState {
  const queryCache = useBrowserQueryCache();
  const [state, setState] = useState<CandidateProposalState>({ status: 'loading' });

  const normalizedSlug = useMemo(() => slug?.trim() ?? '', [slug]);

  useEffect(() => {
    if (!normalizedSlug) {
      setState({ status: 'error', error: 'Identificador da proposta ausente.' });
      return;
    }

    let active = true;

    Promise.all([
      getCandidateBySlug(normalizedSlug, { cache: queryCache }),
      getCandidateProposalDocument(normalizedSlug, { cache: queryCache }),
    ])
      .then(([candidateDoc, document]) => {
        if (!active) {
          return;
        }

        if (!candidateDoc) {
          setState({ status: 'error', error: 'Candidato não encontrado.' });
          return;
        }

        setState({
          status: 'success',
          candidate: toCandidateViewModel(candidateDoc),
          document,
        });
      })
      .catch((error) => {
        console.error('useCandidateProposal failed', { slug: normalizedSlug, error });

        if (!active) {
          return;
        }

        setState({
          status: 'error',
          error: 'Não foi possível carregar a proposta agora.',
        });
      });

    return () => {
      active = false;
    };
  }, [normalizedSlug, queryCache]);

  return state;
}
